'use client';

import React, { useState, useCallback } from 'react';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface GalleryImage {
  src: string;
  alt: string;
  label: string;
  isPlaceholder?: boolean;
  placeholderNote?: string;
}

const galleryImages: GalleryImage[] = [
  { src: 'https://da1mambadetailing.com/assets/222567658_340732361105314_2808448692422444200_n_1751757842604.jpg', alt: 'Heavily stained vehicle interior before professional deep cleaning treatment', label: 'Interior — Before' },
  { src: 'https://da1mambadetailing.com/assets/224636514_559417168420867_2354790812883650572_n_1751757849520.jpg', alt: 'Immaculate vehicle interior after DA1MAMBA deep cleaning service', label: 'Interior — After' },
  { src: 'https://da1mambadetailing.com/assets/259433497_247211697475798_8124183249125052953_n_1751757281389.jpg', alt: 'Heavily corroded wheel before professional restoration service', label: 'Wheel — Before' },
  { src: 'https://da1mambadetailing.com/assets/259226882_321074162852339_2413736059867525475_n_1751757275975.jpg', alt: 'Restored wheel with perfect mirror finish after DA1MAMBA service', label: 'Wheel — After' },
  { src: 'https://da1mambadetailing.com/assets/272985376_361434715437341_1899766350536614438_n_1751758154206.jpg', alt: 'Muddy off-road Jeep before professional exterior cleaning', label: 'Off-Road Jeep — Before' },
  { src: 'https://da1mambadetailing.com/assets/273007642_624158838653013_875377699664385637_n_1751758161383.jpg', alt: 'Clean Jeep with professional lighting effects after detailing', label: 'Off-Road Jeep — After' },
  { src: 'https://da1mambadetailing.com/assets/280047261_531716588413514_7693021305676078627_n_1751758254505.jpg', alt: 'Lamborghini with paint protection masking before correction', label: 'Paint Correction — Before' },
  { src: 'https://da1mambadetailing.com/assets/280216952_1462596080837580_1062062813163839722_n_1751758262081.jpg', alt: 'Lamborghini with flawless paint correction results', label: 'Paint Correction — After' },
  { src: 'https://da1mambadetailing.com/assets/283101701_1071752860080780_8262492719354045677_n_1751758337801.jpg', alt: 'BMW before professional detailing treatment', label: 'BMW — Before' },
  { src: 'https://da1mambadetailing.com/assets/283095294_153481787181047_5566779949128775910_n_1751758344596.jpg', alt: 'BMW transformation after professional DA1MAMBA detailing', label: 'BMW — After' },
  { src: 'https://da1mambadetailing.com/assets/97126918-EC29-40BB-B6B4-2F58B077FB33_1751754684931.jpg', alt: 'Tesla receiving premium foam wash treatment at golden hour', label: 'Tesla Foam Wash' },
  { src: 'https://da1mambadetailing.com/assets/64745EE9-DA2F-493A-B030-5BB30C3F9EA9_1751754690948.jpg', alt: 'Pristine Lamborghini showcasing flawless detailing results', label: 'Lamborghini Detail' },
  { src: 'https://da1mambadetailing.com/assets/IMG_0296_1751754717613.jpg', alt: 'BMW M5 and Rolls-Royce fleet luxury detailing service', label: 'BMW M5 & Rolls-Royce' },
  { src: 'https://da1mambadetailing.com/assets/314609221_166020389450136_2725712082903081790_n_1751758475934.jpg', alt: 'Professional Rolls Royce luxury detailing service', label: 'Rolls Royce Detail' },
  { src: 'https://da1mambadetailing.com/assets/314720331_999311210996670_6450261760225461923_n_1751758482735.jpg', alt: 'Premium Rolls Royce detailing operations in progress', label: 'Rolls Royce Service' },
  { src: 'https://da1mambadetailing.com/assets/image0_1751736526771.jpeg', alt: 'Luxury Rolls-Royce dashboard receiving meticulous detailing', label: 'Rolls-Royce Dashboard' },
  { src: 'https://da1mambadetailing.com/assets/image_1751755535629.jpg', alt: 'Professional hand detailing of engine bay components', label: 'Engine Bay Detail' },
  { src: 'https://da1mambadetailing.com/assets/bentley_interior_1.jpg', alt: 'Bentley luxury interior detailing and restoration service', label: 'Bentley Interior' },
  {
    src: '',
    alt: 'Ceramic Coating Process — placeholder image',
    label: 'Ceramic Coating Process',
    isPlaceholder: true,
    placeholderNote: '[ PLACEHOLDER — Replace with ceramic coating process photo from DA1MAMBA ]',
  },
  {
    src: '',
    alt: 'Showroom Detailing Techniques — placeholder image',
    label: 'Showroom Detailing Techniques',
    isPlaceholder: true,
    placeholderNote: '[ PLACEHOLDER — Replace with showroom detailing techniques photo from DA1MAMBA ]',
  },
];

export default function GalleryPage() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => {
    if (!galleryImages[index].isPlaceholder) setLightboxIndex(index);
  };
  const closeLightbox = () => setLightboxIndex(null);

  const prevImage = useCallback(() => {
    if (lightboxIndex === null) return;
    let prev = (lightboxIndex - 1 + galleryImages.length) % galleryImages.length;
    while (galleryImages[prev].isPlaceholder) {
      prev = (prev - 1 + galleryImages.length) % galleryImages.length;
    }
    setLightboxIndex(prev);
  }, [lightboxIndex]);

  const nextImage = useCallback(() => {
    if (lightboxIndex === null) return;
    let next = (lightboxIndex + 1) % galleryImages.length;
    while (galleryImages[next].isPlaceholder) {
      next = (next + 1) % galleryImages.length;
    }
    setLightboxIndex(next);
  }, [lightboxIndex]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />

      {/* Page Header */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Gallery</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">The Work Speaks for Itself</h1>
        <p className="text-[#9CA3AF] max-w-xl mx-auto mb-10">
          Real vehicles. Real results. Browse before-and-afters from luxury, exotic, and everyday cars we&apos;ve transformed across Orange County.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="sms:9492935721"
            className="w-full sm:w-auto bg-[#4B0082] hover:bg-[#6B21A8] text-white font-semibold px-8 py-3 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Text for a Quote
          </a>
          <a
            href="tel:+19492935721"
            className="w-full sm:w-auto border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black font-semibold px-8 py-3 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Call Now
          </a>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {galleryImages.map((img, i) => (
              img.isPlaceholder ? (
                <div
                  key={i}
                  className="relative aspect-square overflow-hidden bg-[#1a1a1a] border border-dashed border-[#4B0082] flex flex-col items-center justify-center p-4 text-center"
                  title={img.placeholderNote}
                >
                  <svg className="w-8 h-8 text-[#4B0082] mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-[#B8860B] text-xs font-semibold mb-1">{img.label}</p>
                  <p className="text-[#9CA3AF] text-[10px] leading-tight">{img.placeholderNote}</p>
                </div>
              ) : (
                <button
                  key={i}
                  onClick={() => openLightbox(i)}
                  className="relative aspect-square overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[#4B0082]"
                  aria-label={`View ${img.label}`}
                >
                  <Image
                    src={img.src}
                    alt={img.alt}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    unoptimized
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-end">
                    <div className="w-full p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white text-xs font-medium">{img.label}</p>
                    </div>
                  </div>
                </button>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1a0030] via-[#2d0050] to-[#0a0a0a] text-center">
        <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Get Started</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Want Results Like These on Your Vehicle?</h2>
        <p className="text-[#D1D5DB] mb-10">
          We come to you — home, office, or wherever your car is parked. Text or call to get started today.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="sms:9492935721"
            className="w-full sm:w-auto bg-[#4B0082] hover:bg-[#6B21A8] text-white font-semibold px-10 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Text for a Quote
          </a>
          <a
            href="tel:+19492935721"
            className="w-full sm:w-auto border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black font-semibold px-10 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Call Now
          </a>
        </div>
      </section>

      <Footer />

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-[#B8860B] transition-colors z-10 p-2"
            aria-label="Close lightbox"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#B8860B] transition-colors z-10 p-2"
            aria-label="Previous image"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div
            className="relative max-w-4xl max-h-[85vh] w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={galleryImages[lightboxIndex].src}
              alt={galleryImages[lightboxIndex].alt}
              fill
              className="object-contain"
              unoptimized
            />
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#B8860B] transition-colors z-10 p-2"
            aria-label="Next image"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
            <p className="text-white text-sm font-medium">{galleryImages[lightboxIndex].label}</p>
            <p className="text-[#9CA3AF] text-xs mt-1">{lightboxIndex + 1} / {galleryImages.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}
